import { motion } from "framer-motion";
import { Users, ExternalLink, ShieldCheck } from "lucide-react";
import { DISCORD_URL } from "@/lib/config";
import useDiscordWidget from "@/hooks/useDiscordWidget";

function DiscordIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3.2a.077.077 0 0 0-.082.038c-.355.63-.752 1.453-1.029 2.1a18.27 18.27 0 0 0-5.487 0 12.51 12.51 0 0 0-1.045-2.1.08.08 0 0 0-.081-.038A19.736 19.736 0 0 0 5.075 4.37a.07.07 0 0 0-.032.027C1.93 9.046 1.066 13.58 1.49 18.057a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.873-1.295 1.226-1.994a.076.076 0 0 0-.041-.105 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.127c.126-.094.252-.192.372-.291a.074.074 0 0 1 .078-.01c3.927 1.793 8.18 1.793 12.061 0a.073.073 0 0 1 .079.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127c-.598.349-1.22.645-1.873.891a.077.077 0 0 0-.041.106c.36.699.771 1.364 1.225 1.993a.077.077 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.055c.5-5.177-.838-9.674-3.549-13.661a.061.061 0 0 0-.031-.029ZM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.176 1.094 2.157 2.418 0 1.334-.955 2.42-2.157 2.42Zm7.974 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.176 1.094 2.157 2.418 0 1.334-.946 2.42-2.157 2.42Z" />
    </svg>
  );
}

function statusColor(status) {
  switch (status) {
    case "online":
      return "bg-emerald-400";
    case "idle":
      return "bg-amber-400";
    case "dnd":
      return "bg-rose-400";
    default:
      return "bg-slate-400";
  }
}

function AvatarPile({ members }) {
  const visible = members.slice(0, 7);
  const extra = Math.max(0, members.length - visible.length);
  return (
    <div className="flex items-center -space-x-2">
      {visible.map((m) => (
        <div key={m.id} className="relative">
          <img
            src={m.avatar_url}
            alt={m.username}
            className="h-8 w-8 rounded-full ring-2 ring-[#0c0c11] object-cover bg-white/10"
            loading="lazy"
          />
          <span
            className={`absolute -bottom-0.5 -right-0.5 inline-flex h-2.5 w-2.5 rounded-full ring-2 ring-[#0c0c11] ${statusColor(
              m.status
            )}`}
          />
        </div>
      ))}
      {extra > 0 && (
        <div className="grid h-8 w-8 place-items-center rounded-full bg-white/[0.08] ring-2 ring-[#0c0c11] text-[10px] font-semibold text-slate-200">
          +{extra}
        </div>
      )}
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="animate-pulse space-y-3">
      <div className="h-4 w-32 rounded bg-white/10" />
      <div className="h-3 w-48 rounded bg-white/[0.06]" />
    </div>
  );
}

export default function DiscordWidget() {
  const { data, error, loading } = useDiscordWidget();

  const inviteUrl = data?.instant_invite || DISCORD_URL;
  const presence = data?.online ?? 0;
  const totalMembers = data?.total_members ?? 0;
  const members = data?.members || [];
  const name = data?.name || "OriginPS Community";

  return (
    <section
      data-testid="discord-widget-section"
      className="relative mx-auto -mt-10 sm:-mt-16 max-w-6xl px-4 sm:px-6 lg:px-8"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="glass-card ring-border p-5 sm:p-7 lg:p-8 relative overflow-hidden"
      >
        {/* Soft brand glow */}
        <div className="pointer-events-none absolute -top-24 -right-20 h-56 w-56 rounded-full bg-indigo-500/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-amber-500/10 blur-3xl" />

        <div className="relative grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-6 lg:gap-8 items-center">
          {/* LEFT: branding + presence */}
          <div className="min-w-0">
            <div className="flex items-center gap-3 min-w-0">
              <span className="shrink-0 grid h-11 w-11 place-items-center rounded-xl bg-gradient-to-br from-indigo-400 to-indigo-700 ring-1 ring-white/20 shadow-[0_10px_30px_-10px_rgba(99,102,241,0.6)]">
                <DiscordIcon className="h-5 w-5 text-white" />
              </span>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                  Live Discord
                </div>
                <div
                  className="text-base sm:text-lg font-medium text-white truncate"
                  data-testid="discord-server-name"
                >
                  {loading ? "Loading…" : name}
                </div>
              </div>
            </div>

            <div className="mt-5 flex items-end gap-4">
              <div className="flex items-baseline gap-2">
                <span
                  className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-gradient-white"
                  data-testid="discord-presence-count"
                >
                  {loading ? "—" : presence.toLocaleString()}
                </span>
                <span className="text-xs sm:text-sm text-slate-400">
                  online now
                </span>
              </div>
              <span className="hidden sm:inline-flex h-2 w-2 rounded-full bg-emerald-400 pulse-dot mb-2" />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              {loading ? (
                <SkeletonRow />
              ) : error ? (
                <div className="text-xs text-rose-300/80">
                  Couldn&apos;t load live data — widget may be disabled.
                </div>
              ) : (
                <AvatarPile members={members} />
              )}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:flex-wrap gap-2">
              <a
                href={inviteUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="discord-widget-join-btn"
                className="glossy-gold inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold whitespace-nowrap transition-transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <DiscordIcon className="h-4 w-4" />
                Join Discord
              </a>
              <a
                href={inviteUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="discord-widget-open-btn"
                className="ghost-pill inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium whitespace-nowrap transition-all"
              >
                <ExternalLink className="h-4 w-4" />
                Open invite
              </a>
            </div>
          </div>

          {/* RIGHT: Total Members stat + server status row */}
          <div className="grid grid-cols-1 gap-3 sm:gap-4 min-w-0">
            <div className="glass-card ring-border p-5 sm:p-6">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-400">
                <Users className="h-3 w-3 text-amber-300" />
                Total Members
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span
                  className="font-display text-4xl sm:text-5xl font-semibold text-gradient-white"
                  data-testid="discord-widget-online"
                >
                  {loading ? "—" : totalMembers.toLocaleString()}
                </span>
                <span className="text-xs text-slate-400">members</span>
              </div>
            </div>
            <div className="glass-card ring-border p-4">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-400">
                <ShieldCheck className="h-3 w-3 text-emerald-300" />
                Server
              </div>
              <div className="mt-1 flex items-center justify-between gap-2 min-w-0">
                <span className="text-sm font-medium text-white truncate min-w-0">
                  {loading ? "Connecting…" : name}
                </span>
                <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-400/20 px-2 py-0.5 text-[10px] font-medium text-emerald-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />
                  Live
                </span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
